import React from "react";
import Typography from "@material-ui/core/Typography";
import PropTypes from "prop-types";
import { RestrictedToAdmin } from "components/auth";
import FormTextField from "components/form/form-textfield";
import FormCheckbox from "components/form/form-checkbox";
import { PartnerSelect, TimeZoneSelect, TIMEZONES } from "components/select";
import { injectIntl, FormattedMessage } from "react-intl";
import DataGrid, { DataGridItem } from "components/data-grid";
import Card from "components/card";
import withStyles from "components/with-styles";
import styles from "./styles/form.css.js";
import FormComposed from "components/form/form-composed";
import { RadioGroup, RadioButton } from "components/form/form-radiobutton";
import ContactForm, { defaultValues } from "../contact-form";
import AwsElasticIpsForm from "./partial/aws-ips";

const getAllowsConcurrentEventsValue = value => {
  if (value === true) return "true";
  if (value === false) return "false";
  return "";
};

const isLegacyTimeZone = timeZone => !!timeZone && !TIMEZONES.includes(timeZone);

const NewDeploymentForm = ({ intl, values, handleChange, setFieldValue, classes }) => (
  <>
    <DataGridItem>
      <FormTextField
        type="text"
        id="codename"
        name="codename"
        value={values.codename}
        onChange={handleChange}
        label={intl.formatMessage({ id: "deployment.codename" })}
      />
    </DataGridItem>
    <DataGridItem>
      <FormTextField
        type="text"
        id="description"
        name="description"
        value={values.description}
        onChange={handleChange}
        label={intl.formatMessage({ id: "common.description" })}
      />
    </DataGridItem>
    <DataGridItem>
      <FormCheckbox
        id="connectivityFixedIp"
        name="connectivityFixedIp"
        value="connectivityFixedIp"
        checked={values.connectivityFixedIp}
        onChange={handleChange}
        label={intl.formatMessage({ id: "deployment.connectivityFixedIp" })}
      />
    </DataGridItem>
    <DataGridItem>
      <FormCheckbox
        id="mobileBox"
        name="mobileBox"
        value="mobileBox"
        checked={values.mobileBox}
        onChange={handleChange}
        label={intl.formatMessage({ id: "deployment.mobileBox" })}
      />
    </DataGridItem>
    <DataGridItem>
      <FormCheckbox
        id="usuallyDisconnected"
        name="usuallyDisconnected"
        value="usuallyDisconnected"
        checked={values.usuallyDisconnected}
        onChange={handleChange}
        label={intl.formatMessage({ id: "deployment.usuallyDisconnected" })}
      />
    </DataGridItem>
    <DataGridItem>
      <FormCheckbox
        id="allowQod"
        name="allowQod"
        value="allowQod"
        checked={values.allowQod}
        onChange={handleChange}
        label={intl.formatMessage({ id: "deployment.allowQod" })}
      />
    </DataGridItem>
    {values.allowQod && (
      <DataGridItem>
        <FormTextField
          type="text"
          id="msisdnQod"
          name="msisdnQod"
          value={values.msisdnQod}
          onChange={handleChange}
          label={intl.formatMessage({ id: "deployment.msisdnQod" })}
        />
      </DataGridItem>
    )}
    <DataGridItem>
      <RadioGroup
        label={intl.formatMessage({ id: "deployment.allowsConcurrentEvents" })}
        name="allowsConcurrentEvents"
        selectedValue={getAllowsConcurrentEventsValue(values.allowsConcurrentEvents)}
      >
        <RadioButton
          value="true"
          label={intl.formatMessage({ id: "common.yes" })}
          onClick={e => {
            const current = getAllowsConcurrentEventsValue(values.allowsConcurrentEvents);
            setFieldValue("allowsConcurrentEvents", e.target.value === current ? null : true);
          }}
        />
        <RadioButton
          value="false"
          label={intl.formatMessage({ id: "common.no" })}
          onClick={e => {
            const current = getAllowsConcurrentEventsValue(values.allowsConcurrentEvents);
            setFieldValue("allowsConcurrentEvents", e.target.value === current ? null : false);
          }}
        />
      </RadioGroup>
    </DataGridItem>
    <Card className={classes.card}>
      <h4>
        <FormattedMessage id="deployment.proxyPtz" />
      </h4>
      <DataGrid>
        <DataGridItem>
          <FormTextField
            type="text"
            id="proxyPtz.protocol"
            name="proxyPtz.protocol"
            value={values.proxyPtz ? values.proxyPtz.protocol : ""}
            onChange={handleChange}
            label={intl.formatMessage({ id: "deployment.proxyPtz.protocol" })}
          />
        </DataGridItem>
        <DataGridItem>
          <FormTextField
            type="text"
            id="proxyPtz.hostname"
            name="proxyPtz.hostname"
            value={values.proxyPtz ? values.proxyPtz.hostname : ""}
            onChange={handleChange}
            label={intl.formatMessage({ id: "deployment.proxyPtz.hostname" })}
          />
        </DataGridItem>
        <DataGridItem>
          <FormTextField
            type="number"
            inputProps={{ min: 0, maxLength: 4 }}
            onInput={e => {
              if (!e.target.value || isNaN(e.target.value)) {
                e.target.value = 0;
              }
            }}
            id="proxyPtz.port"
            name="proxyPtz.port"
            value={values.proxyPtz && values.proxyPtz.port ? parseInt(values.proxyPtz.port) : 0}
            onChange={handleChange}
            label={intl.formatMessage({ id: "deployment.proxyPtz.port" })}
          />
        </DataGridItem>
      </DataGrid>
    </Card>
    {values.place && (
      <Card className={classes.card}>
        <DataGrid>
          <DataGridItem>
            <FormTextField
              type="text"
              id="place.address"
              name="place.address"
              value={values.place.address}
              onChange={handleChange}
              label={intl.formatMessage({ id: "deployment.place.address" })}
            />
          </DataGridItem>
          <DataGridItem>
            <FormTextField
              type="text"
              id="place.serverNtp"
              name="place.serverNtp"
              value={values.place.serverNtp}
              onChange={handleChange}
              label={intl.formatMessage({ id: "deployment.place.serverNtp" })}
            />
          </DataGridItem>
          <DataGridItem>
            <TimeZoneSelect
              id="place.timeZone"
              name="place.timeZone"
              value={values.place.timeZone}
              onChange={handleChange("place.timeZone")}
              label={intl.formatMessage({ id: "deployment.place.timezone" })}
              error={isLegacyTimeZone(values.place.timeZone)}
              helperText={
                isLegacyTimeZone(values.place.timeZone)
                  ? intl.formatMessage(
                      { id: "deployment.place.timezone.legacyWarning" },
                      { timeZone: values.place.timeZone },
                    )
                  : undefined
              }
            />
          </DataGridItem>
          <DataGridItem>
            <FormTextField
              type="text"
              id="place.name"
              name="place.name"
              value={values.place.name}
              onChange={handleChange}
              label={intl.formatMessage({ id: "deployment.place.name" })}
            />
          </DataGridItem>
          <DataGridItem>
            <FormTextField
              type="text"
              id="place.telephone"
              name="place.telephone"
              value={values.place.telephone}
              onChange={handleChange}
              label={intl.formatMessage({ id: "deployment.place.telephone" })}
            />
          </DataGridItem>
          <DataGridItem>
            <FormCheckbox
              id="place.daylightSaving"
              name="place.daylightSaving"
              value="place.daylightSaving"
              checked={values.place.daylightSaving}
              onChange={handleChange}
              label={intl.formatMessage({ id: "deployment.place.daylightSaving" })}
            />
          </DataGridItem>
          {values.place.geo && (
            <>
              <DataGridItem>
                <FormTextField
                  type="number"
                  id="place.geo.longitude"
                  name="place.geo.longitude"
                  value={values.place.geo.longitude}
                  onChange={handleChange}
                  label={intl.formatMessage({ id: "deployment.place.geo.longitude" })}
                />
              </DataGridItem>
              <DataGridItem>
                <FormTextField
                  type="number"
                  id="place.geo.latitude"
                  name="place.geo.latitude"
                  value={values.place.geo.latitude}
                  onChange={handleChange}
                  label={intl.formatMessage({ id: "deployment.place.geo.latitude" })}
                />
              </DataGridItem>
            </>
          )}
        </DataGrid>
      </Card>
    )}
    <RestrictedToAdmin excludePartners>
      <Card className={classes.card}>
        <DataGrid>
          <DataGridItem>
            <Typography variant="h6">
              <FormattedMessage id="app.page.user.associateToPartner" />
            </Typography>
            <PartnerSelect
              isInsideForm
              label={intl.formatMessage({ id: "app.partner" })}
              onChange={name => value => handleChange(name)(value)}
              value={values.ownedByPartnerId}
            />
          </DataGridItem>
        </DataGrid>
      </Card>
    </RestrictedToAdmin>
    <FormComposed
      addSubformButtonText={intl.formatMessage({ id: "deployment.contacts.add" })}
      name="contacts"
      onChange={handleChange}
      elements={values.contacts}
      emptyElement={defaultValues}
      elementComponent={ContactForm}
    />
    <FormComposed
      addSubformButtonText={intl.formatMessage({ id: "app.page.deployment.form.add.awsElasticIps" })}
      name="awsElasticIps"
      onChange={handleChange}
      elements={values.awsElasticIps}
      emptyElement={""}
      elementComponent={AwsElasticIpsForm}
    />
  </>
);

NewDeploymentForm.propTypes = {
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Callback que se dispara con el cambio del input
   */
  handleChange: PropTypes.func,
};

export default withStyles(styles)(injectIntl(NewDeploymentForm));
